import React, { useContext, useEffect, useMemo, useState } from 'react'
import firebase from 'firebase/app'
import 'firebase/firestore'

import { FirebaseContext } from '../idealline/Firebase'
import DynamicMap3D from '../idealline/DynamicMap3D'
const { sha256Hex } = require('../../utils/telemetryIngestion')
const { getInitialLapSelection } = require('../../utils/telemetryLapSelection')

const formatSeconds = value => {
  if (!Number.isFinite(Number(value))) return '—'
  const seconds = Number(value)
  const minutes = Math.floor(seconds / 60)
  const remainder = (seconds % 60).toFixed(3).padStart(6, '0')
  return minutes ? `${minutes}:${remainder}` : remainder
}

const channelLabel = channel => ({ speed: 'Speed', rpm: 'RPM', gear: 'Gear', throttle: 'Throttle', brake: 'Brake', steering: 'Steering', latAccel: 'Lateral G', longAccel: 'Longitudinal G', yawRate: 'Yaw rate', engineTemp: 'Engine temp', fuelLevel: 'Fuel level', alt: 'Altitude', delta: 'Time delta' }[channel] || channel)

const nearestTracePoint = (trace, pct) => trace?.points?.reduce((best, point) => !best || Math.abs(point.pct - pct) < Math.abs(best.pct - pct) ? point : best, null)

const canonicalize = value => {
  if (Array.isArray(value)) return value.map(canonicalize)
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = canonicalize(value[key])
      return result
    }, {})
  }
  return value
}

const reconstructTraceDocuments = documents => {
  if (!documents.some(document => Number.isInteger(Number(document.chunkIndex)))) return documents
  const groups = documents.reduce((result, document) => {
    const lapNumber = Number(document.lapNumber)
    if (!result.has(lapNumber)) result.set(lapNumber, [])
    result.get(lapNumber).push(document)
    return result
  }, new Map())
  return [...groups.entries()].sort(([left], [right]) => left - right).map(([lapNumber, chunks]) => {
    chunks.sort((left, right) => Number(left.chunkIndex) - Number(right.chunkIndex))
    const expectedChunks = Number(chunks[0]?.chunkCount)
    if (!expectedChunks || chunks.length !== expectedChunks || chunks.some((chunk, index) => Number(chunk.chunkIndex) !== index || Number(chunk.chunkCount) !== expectedChunks)) {
      throw new Error(`Firestore telemetry chunks are incomplete for lap ${lapNumber}`)
    }
    return {
      ...(chunks[0].trace || {}),
      lapNumber,
      isClean: chunks[0].isClean !== false,
      points: chunks.flatMap(chunk => Array.isArray(chunk.points) ? chunk.points : []),
    }
  })
}

const loadFirestoreTelemetryReport = async (telemetryId, reportIndex, expectedGeneration, expectedCount, expectedDigest) => {
  if (!expectedGeneration) throw new Error('Firestore telemetry generation is unavailable')
  const snapshot = await firebase.firestore()
    .collection('telemetry').doc(telemetryId)
    .collection('laps').where('generation', '==', expectedGeneration).get()
  const documents = snapshot.docs.map(document => document.data())
  if (!documents.length || documents.length !== Number(expectedCount) || documents.some(document => document.generation !== expectedGeneration)) {
    throw new Error('Firestore telemetry traces are incomplete')
  }
  const traces = reconstructTraceDocuments(documents)
  if (expectedDigest) {
    const digest = await sha256Hex(JSON.stringify(canonicalize(traces)))
    if (digest !== expectedDigest) throw new Error('Firestore telemetry trace integrity check failed')
  }
  return { ...reportIndex, traces }
}

const MetricCard = ({ label, value, detail }) => (
  <article className="session-report__metric">
    <span>{label}</span>
    <strong>{value}</strong>
    {detail && <small>{detail}</small>}
  </article>
)

const TraceChart = ({ traces, channel, focusLap, referenceLap, cursorPct, onCursorChange }) => {
  const width = 920
  const height = 280
  const padding = 36
  const visible = [focusLap, referenceLap]
    .filter((lapNumber, index, laps) => lapNumber != null && laps.indexOf(lapNumber) === index)
    .map(lapNumber => traces.find(trace => trace.lapNumber === lapNumber))
    .filter(Boolean)
  const values = visible.flatMap(trace => trace.points.map(point => point[channel])).filter(Number.isFinite)
  const min = values.length ? Math.min(...values) : 0
  const max = values.length ? Math.max(...values) : 1
  const range = max - min || 1
  const colors = ['#0f6fec', '#111827']

  return (
    <div className="session-report__chart-wrap">
      <svg className="session-report__chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${channelLabel(channel)} by lap distance`}
        onMouseMove={event => {
          const bounds = event.currentTarget.getBoundingClientRect()
          onCursorChange(Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)))
        }}>
        {[0, 1, 2, 3, 4].map(row => {
          const y = padding + row * ((height - padding * 2) / 4)
          const label = max - (range * row / 4)
          return <g key={row}><line x1={padding} y1={y} x2={width - padding} y2={y} stroke="#e5e7eb" /><text x="2" y={y + 4} fontSize="11" fill="#6b7280">{Math.round(label)}</text></g>
        })}
        {visible.map((trace, traceIndex) => {
          const coordinates = trace.points.map((point, index) => {
            const progress = Number.isFinite(point.pct) ? point.pct : index / Math.max(1, trace.points.length - 1)
            const x = padding + progress * (width - padding * 2)
            const value = Number.isFinite(point[channel]) ? point[channel] : min
            const y = height - padding - ((value - min) / range) * (height - padding * 2)
            return `${x},${y}`
          }).join(' ')
          return <polyline key={trace.lapNumber} points={coordinates} fill="none" stroke={colors[traceIndex % colors.length]} strokeWidth="2.5" opacity="0.9" />
        })}
        <line x1={padding + cursorPct * (width - padding * 2)} x2={padding + cursorPct * (width - padding * 2)} y1={padding} y2={height - padding} stroke="#f59e0b" strokeWidth="2" />
      </svg>
      <div className="session-report__legend">
        {visible.map((trace, index) => <span key={trace.lapNumber}><i style={{ background: colors[index % colors.length] }} />Lap {trace.lapNumber}</span>)}
      </div>
    </div>
  )
}

const LapTimeChart = ({ laps }) => {
  const fastest = Math.min(...laps.map(lap => lap.lapTimeSeconds))
  const slowest = Math.max(...laps.map(lap => lap.lapTimeSeconds))
  const range = slowest - fastest || 1
  return (
    <div className="session-report__lap-bars">
      {laps.map(lap => (
        <div className="session-report__lap-bar" key={lap.lapNumber}>
          <span>Lap {lap.lapNumber}</span>
          <div><i style={{ width: `${45 + ((lap.lapTimeSeconds - fastest) / range) * 55}%` }} /></div>
          <strong>{formatSeconds(lap.lapTimeSeconds)}</strong>
          <small>{lap.isClean ? 'Clean' : 'Incident'}</small>
        </div>
      ))}
    </div>
  )
}

const TelemetrySessionReport = () => {
  const auth = useContext(FirebaseContext)
  const [record, setRecord] = useState(null)
  const [error, setError] = useState(null)
  const [reportLoadAttempt, setReportLoadAttempt] = useState(0)
  const [activeTab, setActiveTab] = useState('overview')
  const [channel, setChannel] = useState('speed')
  const [mapLap, setMapLap] = useState(null)
  const [referenceLap, setReferenceLap] = useState(null)
  const [cursorPct, setCursorPct] = useState(0)
  const [selectedCorner, setSelectedCorner] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [mapFailed, setMapFailed] = useState(false)

  const params = typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search)
  const telemetryId = params.get('id')
  const localFixture = params.get('fixture')

  useEffect(() => {
    setError(null)
    if (process.env.NODE_ENV === 'development' && localFixture && /^[a-z0-9-]+$/.test(localFixture)) {
      let disposed = false
      fetch(`/__telemetry-fixtures/${localFixture}.json`, { cache: 'no-store' })
        .then(response => {
          if (!response.ok) throw new Error('Local telemetry fixture was not found')
          return response.json()
        })
        .then(report => {
          if (disposed) return
          setRecord({ id: localFixture, status: 'ready', derivedReport: report })
          const { focusLap, referenceLap: initialReferenceLap } = getInitialLapSelection(report)
          setMapLap(focusLap)
          setReferenceLap(initialReferenceLap)
        })
        .catch(fixtureError => {
          if (!disposed) setError(fixtureError.message)
        })
      return () => { disposed = true }
    }
    if (auth.user === false) {
      auth.requireAuth(() => {})
      return undefined
    }
    if (!auth.user || !telemetryId) return undefined

    let disposed = false
    let loadSequence = 0
    const unsubscribe = firebase.firestore().collection('telemetry').doc(telemetryId).onSnapshot(async snapshot => {
      const sequence = ++loadSequence
      if (!snapshot.exists) {
        setError('This telemetry report was not found or you do not have access to it.')
        return
      }
      const next = { id: snapshot.id, ...snapshot.data() }
      if (next.status !== 'ready') {
        setRecord(current => current?.id === next.id && current?.derivedReport?.traces?.length ? current : next)
        setError(next.status === 'failed' ? 'We could not process this session. Your original upload is safe.' : null)
        return
      }

      try {
        let report = next.derivedReport
        if (next.traceStorage === 'firestore_laps_v2' || report?.traceStorage === 'firestore_laps_v2') {
          report = await loadFirestoreTelemetryReport(
            telemetryId,
            report,
            next.traceGeneration || report?.traceGeneration,
            next.traceCount || report?.traceCount,
            next.traceDigest || report?.traceDigest,
          )
        }
        if (!Array.isArray(report?.traces) || report.traces.length === 0) throw new Error('Telemetry traces are incomplete')
        if (disposed || sequence !== loadSequence) return
        setError(null)
        setRecord({ ...next, derivedReport: report })
        const { focusLap, referenceLap: initialReferenceLap } = getInitialLapSelection(report)
        setMapLap(focusLap)
        setReferenceLap(initialReferenceLap)
      } catch (reportError) {
        console.error('Could not load the complete telemetry report:', reportError)
        if (!disposed && sequence === loadSequence) setError('We could not open this report yet. Your original upload is safe.')
      }
    }, () => setError('We could not open this report yet. Your original upload is safe.'))
    return () => {
      disposed = true
      unsubscribe()
    }
  }, [auth.user, telemetryId, localFixture, reportLoadAttempt])

  useEffect(() => {
    if (!isPlaying) return undefined
    const timer = window.setInterval(() => setCursorPct(current => current >= 1 ? 0 : Math.min(1, current + 0.005)), 50)
    return () => window.clearInterval(timer)
  }, [isPlaying])

  const report = record?.derivedReport
  const availableTraces = report?.traces || []
  const availableTraceLaps = availableTraces.map(trace => trace.lapNumber)
  const activeLapNumber = availableTraceLaps.includes(mapLap) ? mapLap : availableTraceLaps[0]
  const activeReferenceLap = availableTraceLaps.includes(referenceLap) ? referenceLap : availableTraceLaps[0]
  const mapTrace = useMemo(() => report?.traces?.find(trace => trace.lapNumber === activeLapNumber), [report, activeLapNumber])
  const referenceTrace = useMemo(() => report?.traces?.find(trace => trace.lapNumber === activeReferenceLap), [report, activeReferenceLap])
  const mapPoints = useMemo(() => (mapTrace?.points || [])
    .filter(point => point.lat != null && point.lng != null && Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng)))
    .map(point => ({
      ...point,
      lat: Number(point.lat),
      lng: Number(point.lng),
      altitude: point.alt ?? 0,
      distanceM: Number.isFinite(point.dist) ? point.dist : point.pct * (report?.track?.lengthMeters || 0),
      type: point.brake > 0.05 ? 'brake' : point.throttle > 0.8 ? 'acc' : 'coast',
    })), [mapTrace, report])
  const mapCenter = useMemo(() => {
    if (!mapPoints.length) return { lat: null, lng: null }
    const latitudes = mapPoints.map(point => Number(point.lat)).filter(Number.isFinite)
    const longitudes = mapPoints.map(point => Number(point.lng)).filter(Number.isFinite)
    return {
      lat: (Math.min(...latitudes) + Math.max(...latitudes)) / 2,
      lng: (Math.min(...longitudes) + Math.max(...longitudes)) / 2,
    }
  }, [mapPoints])

  useEffect(() => {
    setMapFailed(false)
  }, [activeLapNumber])
  const selectedPointIndex = mapPoints.length ? mapPoints.reduce((best, point, index) => Math.abs(point.pct - cursorPct) < Math.abs(mapPoints[best].pct - cursorPct) ? index : best, 0) : -1
  const cursorPoint = mapPoints[selectedPointIndex]
  const referencePoint = nearestTracePoint(referenceTrace, cursorPct)
  const cursorDelta = Number.isFinite(cursorPoint?.elapsedSeconds) && Number.isFinite(referencePoint?.elapsedSeconds) ? cursorPoint.elapsedSeconds - referencePoint.elapsedSeconds : null
  const activeCorner = report?.corners?.find(corner => corner.number === selectedCorner)
  const selectedRange = activeCorner ? {
    startM: activeCorner.entryPct * (report.track.lengthMeters || mapPoints[mapPoints.length - 1]?.distanceM || 0),
    endM: activeCorner.exitPct * (report.track.lengthMeters || mapPoints[mapPoints.length - 1]?.distanceM || 0),
  } : null

  const selectCorner = (corner, revealMap = false) => {
    setSelectedCorner(corner.number)
    setCursorPct(corner.apexPct)
    setIsPlaying(false)
    if (revealMap) window.requestAnimationFrame(() => document.querySelector('.session-report__map-shell')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }

  if (error) return (
    <main className="session-report session-report--state">
      <p className="session-report__eyebrow">Telemetry report</p>
      <h1>We could not open this report yet</h1>
      <p>{error}</p>
      <div className="session-report__state-actions">
        <button type="button" onClick={() => setReportLoadAttempt(attempt => attempt + 1)}>Try again</button>
        <a href="/profile">Back to your sessions</a>
      </div>
    </main>
  )
  if (!report) {
    const isProcessing = record?.status === 'processing' || record?.status === 'queued' || record?.status === 'uploaded'
    return (
      <main className="session-report session-report--state" role="status" aria-live="polite">
        <p className="session-report__eyebrow">Telemetry report</p>
        <h1>{isProcessing ? 'Processing your session' : 'Preparing your telemetry report'}</h1>
        <p>{isProcessing ? 'We are reading the measured laps and building your report. You can safely leave this page and return from your profile.' : 'Loading your measured laps.'}</p>
      </main>
    )
  }

  const { summary } = report
  const speedKmh = value => report.units?.speed === 'km/h' ? value : value * 3.6
  const lapTimes = report.laps.map(lap => lap.lapTimeSeconds).filter(Number.isFinite)
  const lapSpread = lapTimes.length > 1 ? Math.max(...lapTimes) - Math.min(...lapTimes) : null
  return (
    <main className="session-report">
      <header className="session-report__hero">
        <div>
          <p className="session-report__eyebrow">{report.source === 'alfano' ? 'Alfano karting analysis' : 'iRacing telemetry analysis'}</p>
          <h1>{report.track.name || 'Telemetry session'}</h1>
          <p>{[report.track.configuration, report.vehicle.name, `${summary.totalLapCount} measured ${summary.totalLapCount === 1 ? 'lap' : 'laps'}`].filter(Boolean).join(' · ')}</p>
        </div>
      </header>

      <section className="session-report__map-shell" aria-label="Lap comparison workspace">
        <div className="session-report__map-topbar">
          <div className="session-report__map-title"><span>Lap workspace</span><strong>Focus lap {activeLapNumber} against lap {activeReferenceLap}</strong></div>
          <div className="session-report__lap-controls">
            <label><span>Focus lap</span><select value={activeLapNumber || ''} onChange={event => {
              const lapNumber = Number(event.target.value)
              setMapLap(lapNumber)
              if (lapNumber === activeReferenceLap) setReferenceLap(availableTraceLaps.find(number => number !== lapNumber) || lapNumber)

              setCursorPct(0)
              setSelectedCorner(null)
            }} aria-label="Focus lap">
              {availableTraces.map(trace => <option value={trace.lapNumber} key={trace.lapNumber}>Lap {trace.lapNumber} · {formatSeconds(report.laps.find(lap => lap.lapNumber === trace.lapNumber)?.lapTimeSeconds)}</option>)}
            </select></label>
            <span className="session-report__against">against</span>
            <label><span>Reference lap</span><select value={activeReferenceLap || ''} onChange={event => {
              const lapNumber = Number(event.target.value)
              setReferenceLap(lapNumber)
              if (lapNumber === activeLapNumber) setMapLap(availableTraceLaps.find(number => number !== lapNumber) || lapNumber)

            }} aria-label="Reference lap">
              {availableTraces.map(trace => <option value={trace.lapNumber} key={trace.lapNumber}>Lap {trace.lapNumber} · {formatSeconds(report.laps.find(lap => lap.lapNumber === trace.lapNumber)?.lapTimeSeconds)}</option>)}
            </select></label>
            <small>Available traces only</small>
          </div>
        </div>
        <div className="session-report__map">
          {mapPoints.length > 1 && process.env.GATSBY_GMAPS_API_KEY && !mapFailed &&
            <div className="session-report__map-3d">
              <DynamicMap3D apikey={process.env.GATSBY_GMAPS_API_KEY} points={mapPoints} lat={mapCenter.lat} lng={mapCenter.lng} zoom={17} embedLayout fillContainer showControls readOnly preserveMeasuredGeometry selectedPointIndex={selectedPointIndex} selectedRange={selectedRange} onPointSelect={index => setCursorPct(mapPoints[index]?.pct || 0)} onLoadError={() => setMapFailed(true)} />
            </div>}
          {mapPoints.length < 2 && <div className="session-report__map-unavailable" role="status"><strong>No GPS racing line was found</strong><span>The rest of the measured session is still available below.</span></div>}
          {mapPoints.length > 1 && (!process.env.GATSBY_GMAPS_API_KEY || mapFailed) && <div className="session-report__map-unavailable" role="status"><strong>The 3D racing line is temporarily unavailable</strong><span>Try reopening this report in a moment.</span></div>}
          <div className="session-report__map-legend" aria-label="Racing line color key"><span><i className="is-throttle" />Throttle</span><span><i className="is-coast" />Coast</span><span><i className="is-brake" />Brake</span></div>
        </div>
        <div className="session-report__transport">
          <button type="button" className="session-report__play" onClick={() => setIsPlaying(value => !value)}>{isPlaying ? 'Pause' : 'Play lap'}</button>
          <label className="session-report__scrubber"><span className="sr-only">Track position</span><input type="range" min="0" max="1000" value={Math.round(cursorPct * 1000)} onChange={event => { setCursorPct(Number(event.target.value) / 1000); setSelectedCorner(null) }} /></label>
          <div className="session-report__cursor-readout" aria-live="polite">
            <span><small>Distance</small><strong>{Math.round(cursorPoint?.distanceM || 0)} m</strong></span>
            <span><small>Speed</small><strong>{Number.isFinite(cursorPoint?.speed) ? `${Math.round(speedKmh(cursorPoint.speed))} km/h` : '—'}</strong></span>
            <span><small>Gear</small><strong>{cursorPoint?.gear ?? '—'}</strong></span>
            <span><small>Brake</small><strong>{Number.isFinite(cursorPoint?.brake) ? `${Math.round(cursorPoint.brake * 100)}%` : '—'}</strong></span>
            <span><small>Throttle</small><strong>{Number.isFinite(cursorPoint?.throttle) ? `${Math.round(cursorPoint.throttle * 100)}%` : '—'}</strong></span>
            <span className={cursorDelta > 0 ? 'is-loss' : 'is-gain'}><small>Delta</small><strong>{Number.isFinite(cursorDelta) ? `${cursorDelta >= 0 ? '+' : ''}${cursorDelta.toFixed(3)}s` : '—'}</strong></span>
          </div>
        </div>
        <div className="session-report__corner-strip" aria-label="Detected corners">
          {(report.corners || []).map(corner => {
            const focusCornerPoint = nearestTracePoint(mapTrace, corner.apexPct)
            const referenceCornerPoint = nearestTracePoint(referenceTrace, corner.apexPct)
            const speedDelta = Number.isFinite(focusCornerPoint?.speed) && Number.isFinite(referenceCornerPoint?.speed) ? focusCornerPoint.speed - referenceCornerPoint.speed : null
            const deltaKmh = Number.isFinite(speedDelta) ? Math.round(speedKmh(speedDelta)) : null
            return <button key={corner.number} className={selectedCorner === corner.number ? 'is-active' : ''} onClick={() => selectCorner(corner)}><span>Corner {corner.number}</span><strong>{Math.round(speedKmh(corner.minimumSpeed))} km/h</strong><small>Gear {corner.minimumGear ?? '—'} · {deltaKmh == null ? 'No comparison' : `${deltaKmh >= 0 ? '+' : ''}${deltaKmh} km/h`}</small></button>
          })}
        </div>
      </section>

      <section className="session-report__metrics" aria-label="Session summary">
        <MetricCard label="Best lap" value={formatSeconds(summary.bestLapSeconds)} detail={`Lap ${summary.bestLapNumber}`} />
        <MetricCard label="Best combined thirds" value={formatSeconds(summary.theoreticalBestSeconds)} detail="Derived from equal-distance sections" />
        {summary.totalLapCount >= 3
          ? <MetricCard label="Repeatability" value={`${summary.consistencyScore}/100`} detail={`${summary.cleanLapCount} clean laps`} />
          : <MetricCard label="Lap gap" value={lapSpread == null ? '—' : `${lapSpread.toFixed(3)}s`} detail="More laps improve repeatability" />}
        <MetricCard label="Maximum speed" value={summary.maxSpeed == null ? '—' : `${Math.round(speedKmh(summary.maxSpeed))} km/h`} detail="Measured peak" />
      </section>

      <nav className="session-report__tabs" aria-label="Report sections">
        {[['overview', 'Session'], ['traces', 'Inputs'], ['compare', 'Laps and sections'], ['analysis', 'Corners'], ['session', 'File details']].map(([key, label]) => (
          <button key={key} className={activeTab === key ? 'is-active' : ''} onClick={() => setActiveTab(key)}>{label}</button>
        ))}
      </nav>

      {activeTab === 'overview' && <section className="session-report__grid">
        <article className="session-report__panel session-report__panel--wide"><h2>Lap times</h2><p>Every timed lap, with incidents kept visible instead of silently removed.</p><LapTimeChart laps={report.laps} /></article>
        <article className="session-report__panel"><h2>Best sectors</h2><p>The fastest measured third of the lap from this session.</p>{report.sectors.map(sector => <div className="session-report__sector" key={sector.number}><span>Sector {sector.number}</span><strong>{formatSeconds(sector.bestSeconds)}</strong></div>)}</article>
        <article className="session-report__panel"><h2>Measured analysis</h2><p>Racing line, laps, sectors, inputs, braking zones, gear shifts and distribution data are derived from the uploaded telemetry.</p><div className="session-report__pending"><strong>{report.racingLine?.pointCount || 0} mapped samples</strong><span>{report.corners?.length || 0} corners and {report.events?.brakingZones?.length || 0} braking zones detected on the reference lap.</span></div></article>
      </section>}

      {activeTab === 'traces' && <section className="session-report__panel session-report__panel--full">
        <div className="session-report__panel-head"><div><h2>Interactive traces</h2><p>Compare measured channels against lap distance.</p></div><div className="session-report__channel-picker">{report.channels.map(item => <button key={item} className={channel === item ? 'is-active' : ''} onClick={() => setChannel(item)}>{channelLabel(item)}</button>)}</div></div>
        <div className="session-report__comparison-key"><span className="is-focus">Focus lap {activeLapNumber}</span><span className="is-reference">Reference lap {activeReferenceLap}</span><small>Move across the chart to inspect the same position on the map.</small></div>
        <TraceChart traces={report.traces} channel={channel} focusLap={activeLapNumber} referenceLap={activeReferenceLap} cursorPct={cursorPct} onCursorChange={setCursorPct} />
      </section>}

      {activeTab === 'compare' && <section className="session-report__panel session-report__panel--full">
        <h2>Lap comparison</h2><p>Measured differences from the fastest clean lap.</p>
        <div className="session-report__table-wrap"><table><thead><tr><th>Lap</th><th>Time</th><th>Delta</th><th>S1</th><th>S2</th><th>S3</th><th>Max speed</th><th>Status</th></tr></thead><tbody>{report.laps.map(lap => <tr key={lap.lapNumber}><td>{lap.lapNumber}</td><td>{formatSeconds(lap.lapTimeSeconds)}</td><td>{lap.lapTimeSeconds === summary.bestLapSeconds ? 'Best' : `+${(lap.lapTimeSeconds - summary.bestLapSeconds).toFixed(3)}s`}</td>{lap.sectors.map((sector, index) => <td key={index}>{formatSeconds(sector)}</td>)}<td>{lap.maxSpeed == null ? '—' : `${Math.round(speedKmh(lap.maxSpeed))} km/h`}</td><td>{lap.isClean ? 'Clean' : 'Incident'}</td></tr>)}</tbody></table></div>
        <h2>Sector heatmap</h2><p>Each cell shows time lost to the session best for that sector.</p>
        <div className="session-report__table-wrap"><table><thead><tr><th>Lap</th><th>Sector 1</th><th>Sector 2</th><th>Sector 3</th></tr></thead><tbody>{report.sectorHeatmap.map(lap => <tr key={lap.lapNumber}><td>{lap.lapNumber}</td>{lap.sectors.map(sector => <td key={sector.number} style={{ background: `rgba(217,119,6,${Math.min(0.45, sector.deltaToBest / 3)})` }}>{sector.deltaToBest ? `+${sector.deltaToBest.toFixed(3)}s` : 'Best'}</td>)}</tr>)}</tbody></table></div>
      </section>}

      {activeTab === 'analysis' && <section className="session-report__grid">
        <article className="session-report__panel session-report__panel--wide"><h2>Corner analysis</h2><p>Detected from local speed minima on the fastest measured lap. Select a corner to highlight it on the map.</p><div className="session-report__table-wrap"><table><thead><tr><th>Corner</th><th>Entry</th><th>Minimum</th><th>Exit</th><th>Gear</th><th>Peak brake</th><th>Lap speed delta</th></tr></thead><tbody>{report.corners.map(corner => <tr key={corner.number} role="button" tabIndex="0" onClick={() => selectCorner(corner, true)} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') selectCorner(corner, true) }}><td>{corner.number}</td><td>{Math.round(speedKmh(corner.entrySpeed))} km/h</td><td>{Math.round(speedKmh(corner.minimumSpeed))} km/h</td><td>{Math.round(speedKmh(corner.exitSpeed))} km/h</td><td>{corner.minimumGear ?? '—'}</td><td>{corner.brakePeak == null ? '—' : `${Math.round(corner.brakePeak * 100)}%`}</td><td>{(() => { const focus = nearestTracePoint(mapTrace, corner.apexPct); const reference = nearestTracePoint(referenceTrace, corner.apexPct); const delta = Number.isFinite(focus?.speed) && Number.isFinite(reference?.speed) ? focus.speed - reference.speed : null; return Number.isFinite(delta) ? `${delta >= 0 ? '+' : ''}${Math.round(speedKmh(delta))} km/h` : '—' })()}</td></tr>)}</tbody></table></div></article>
        <article className="session-report__panel"><h2>Braking zones</h2><p>{report.events.brakingZones.length} measured brake applications on the reference lap.</p>{report.events.brakingZones.map(zone => <div className="session-report__sector" key={zone.number}><span>Zone {zone.number}</span><strong>{Math.round(zone.peakBrake * 100)}%</strong></div>)}</article>
        <article className="session-report__panel"><h2>Gear shifts</h2><p>{report.events.gearShifts.length} shifts detected from the measured gear channel.</p>{report.distributions.gear.map(item => <div className="session-report__sector" key={item.gear}><span>Gear {item.gear}</span><strong>{item.sampleCount} samples</strong></div>)}</article>
        <article className="session-report__panel session-report__panel--wide"><h2>RPM distribution</h2><p>Reference lap samples grouped into 1,000 RPM bands.</p>{report.distributions.rpm.map(band => <div className="session-report__sector" key={band.minimum}><span>{band.minimum.toLocaleString()} to {band.maximum.toLocaleString()} RPM</span><strong>{band.sampleCount}</strong></div>)}</article>
      </section>}

      {activeTab === 'session' && <section className="session-report__grid"><article className="session-report__panel"><h2>Source file</h2><dl><div><dt>Format</dt><dd>{report.source.toUpperCase()}</dd></div><div><dt>File</dt><dd>{report.sourceFile.name || 'Original telemetry'}</dd></div><div><dt>Stored</dt><dd>{report.sourceFile.retention === 'normalized_only' ? 'Normalized telemetry retained; original file unavailable' : 'Private to your account'}</dd></div><div><dt>Schema</dt><dd>Version {report.schemaVersion}</dd></div></dl></article><article className="session-report__panel"><h2>Available channels</h2><div className="session-report__channel-list">{report.channels.map(item => <span key={item}>{channelLabel(item)}</span>)}</div><p>These are programmatically extracted from the uploaded file. No generated coaching is included yet.</p></article></section>}
    </main>
  )
}

export default TelemetrySessionReport
